import React, { useState } from 'react';

// ── Calendar / date-heure picker (maison, pas de bibliothèque) ─────────────────
// Campagne-timezone : tout est saisi dans le fuseau de la campagne, quoi que
// dise le navigateur de l'agent. Même modèle mural que backend utils/tz.js.
export function CalendarPicker({ value, onChange, tz = 'America/Toronto' }) {
  const tzNowParts = (date = new Date()) => {
    const parts = new Intl.DateTimeFormat('en-CA', {
      timeZone: tz, year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', hour12: false
    }).formatToParts(date);
    const p = {}; parts.forEach(it => p[it.type] = it.value);
    return { date: `${p.year}-${p.month}-${p.day}`, time: `${(parseInt(p.hour, 10) % 24).toString().padStart(2, '0')}:${p.minute}` };
  };
  const instantInTz = (ms) => { const { date, time } = tzNowParts(new Date(ms)); return `${date}T${time}`; };
  // Arithmétique de date ancrée à midi UTC : les décalages DST (±1 h) ne
  // changent jamais le jour calendaire.
  const addDays = (dateStr, n) => {
    const d = new Date(`${dateStr}T12:00:00Z`);
    d.setUTCDate(d.getUTCDate() + n);
    return d.toISOString().slice(0, 10);
  };

  const nowParts = tzNowParts();
  const [view, setView]       = useState('quick'); // 'quick' | 'calendar'
  const [selDate, setSelDate] = useState('');
  const [selTime, setSelTime] = useState('09:00');
  const [month, setMonth]     = useState(parseInt(nowParts.date.slice(5, 7), 10) - 1);
  const [year, setYear]       = useState(parseInt(nowParts.date.slice(0, 4), 10));

  // Choix rapides (Karim 2026-10-14) : 1h / 2h / 4h / demain / 1 semaine.
  const quickOptions = [
    { label: 'Dans 1 heure',   fn: () => onChange(instantInTz(Date.now() + 3600000)) },
    { label: 'Dans 2 heures', fn: () => onChange(instantInTz(Date.now() + 2 * 3600000)) },
    { label: 'Dans 4 heures', fn: () => onChange(instantInTz(Date.now() + 4 * 3600000)) },
    { label: 'Demain 9 h',     fn: () => onChange(`${addDays(nowParts.date, 1)}T09:00`) },
    { label: 'Dans 1 semaine', fn: () => onChange(instantInTz(Date.now() + 7 * 86400000)) },
  ];

  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const firstDay    = new Date(year, month, 1).getDay();
  const months      = ['Jan','Fév','Mar','Avr','Mai','Juin','Juil','Août','Sep','Oct','Nov','Déc'];
  const days        = ['Di','Lu','Ma','Me','Je','Ve','Sa'];

  const handleCalendarDay = (d) => {
    const dateStr = `${year}-${String(month + 1).padStart(2,'0')}-${String(d).padStart(2,'0')}`;
    setSelDate(dateStr);
    onChange(`${dateStr}T${selTime}`);
    setView('quick');
  };

  const handleTimeChange = (t) => {
    setSelTime(t);
    if (selDate) onChange(`${selDate}T${t}`);
  };

  const selectedDate = value ? value.slice(0, 10) : '';
  const label = value
    ? (() => {
        const [datePart, timePart] = value.split('T');
        const [y,m,d] = datePart.split('-');
        const monthsFr = ['Jan','Fév','Mar','Avr','Mai','Juin','Juil','Août','Sep','Oct','Nov','Déc'];
        return `${d} ${monthsFr[parseInt(m)-1]}, ${timePart?.slice(0,5) || ''}`;
      })()
    : null;

  return (
    <div>
      {label && (
        <div style={{ marginBottom: 10, padding: '8px 14px', background: 'linear-gradient(135deg,rgba(124,58,237,0.1),rgba(99,102,241,0.07))', borderRadius: 10, border: '2px solid rgba(124,58,237,0.25)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <div style={{ fontSize: 9, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--purple)', marginBottom: 2 }}>Prévu le</div>
            <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text)' }}>📅 {label}</div>
          </div>
          <button onClick={() => onChange('')} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 16, color: 'var(--text2)' }}>✕</button>
        </div>
      )}

      <div style={{ display: 'flex', gap: 4, marginBottom: 12 }}>
        {[['quick','⚡ Sélection rapide'],['calendar','📅 Calendrier']].map(([v,l]) => (
          <button key={v} onClick={() => setView(v)} style={{
            flex: 1, height: 32, borderRadius: 100, border: 'none', cursor: 'pointer',
            fontWeight: 700, fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.04em',
            background: view === v ? 'linear-gradient(135deg,var(--purple),var(--indigo))' : 'var(--bg-input)',
            color: view === v ? '#fff' : 'var(--text2)',
            boxShadow: view === v ? '0 4px 12px rgba(124,58,237,0.3)' : 'none',
            transition: 'all 0.2s'
          }}>
            {l}
          </button>
        ))}
      </div>

      {view === 'quick' && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
          {quickOptions.map(opt => (
            <button key={opt.label} onClick={opt.fn} style={{
              padding: '10px 12px', borderRadius: 10, border: '2px solid var(--border)',
              background: 'var(--bg-input)', cursor: 'pointer', textAlign: 'left',
              fontSize: 12, fontWeight: 600, color: 'var(--text)',
              transition: 'all 0.2s', display: 'flex', alignItems: 'center', gap: 7
            }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--purple)'; e.currentTarget.style.background = 'rgba(124,58,237,0.06)'; e.currentTarget.style.color = 'var(--purple)'; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.background = 'var(--bg-input)'; e.currentTarget.style.color = 'var(--text)'; }}
            >
              <span style={{ fontSize: 16 }}>
                {opt.label.includes('heure') ? '⏰' : opt.label.includes("Aujourd'hui") ? '📌' : opt.label.includes('Lundi') ? '📆' : '📅'}
              </span>
              {opt.label}
            </button>
          ))}
        </div>
      )}

      {view === 'calendar' && (
        <div style={{ background: 'var(--bg-input)', borderRadius: 14, border: '2px solid var(--border)', padding: 14 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <button onClick={() => { if (month === 0) { setMonth(11); setYear(y => y - 1); } else setMonth(m => m - 1); }}
              style={{ background: 'none', border: '2px solid var(--border)', borderRadius: 8, width: 28, height: 28, cursor: 'pointer', fontSize: 14, color: 'var(--text)' }}>‹</button>
            <span style={{ fontWeight: 800, fontSize: 14, color: 'var(--text)' }}>{months[month]} {year}</span>
            <button onClick={() => { if (month === 11) { setMonth(0); setYear(y => y + 1); } else setMonth(m => m + 1); }}
              style={{ background: 'none', border: '2px solid var(--border)', borderRadius: 8, width: 28, height: 28, cursor: 'pointer', fontSize: 14, color: 'var(--text)' }}>›</button>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7,1fr)', gap: 2, marginBottom: 6 }}>
            {days.map(d => <div key={d} style={{ textAlign: 'center', fontSize: 10, fontWeight: 800, color: 'var(--text2)', padding: '4px 0' }}>{d}</div>)}
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7,1fr)', gap: 2 }}>
            {Array(firstDay).fill(null).map((_, i) => <div key={`e${i}`} />)}
            {Array.from({ length: daysInMonth }, (_, i) => i + 1).map(d => {
              const dateStr = `${year}-${String(month + 1).padStart(2,'0')}-${String(d).padStart(2,'0')}`;
              const isToday = dateStr === nowParts.date;
              const isSel   = dateStr === selectedDate;
              const isPast  = new Date(`${dateStr}T12:00:00Z`) < new Date(`${nowParts.date}T12:00:00Z`);
              return (
                <button key={d} onClick={() => !isPast && handleCalendarDay(d)} disabled={isPast} style={{
                  height: 30, borderRadius: 8, border: 'none', cursor: isPast ? 'not-allowed' : 'pointer', fontSize: 12, fontWeight: isSel ? 800 : isToday ? 700 : 500,
                  background: isSel ? 'linear-gradient(135deg,var(--purple),var(--indigo))' : isToday ? 'rgba(124,58,237,0.12)' : 'transparent',
                  color: isSel ? '#fff' : isPast ? 'var(--muted)' : isToday ? 'var(--purple)' : 'var(--text)',
                  boxShadow: isSel ? '0 4px 10px rgba(124,58,237,0.3)' : 'none',
                  transition: 'all 0.15s'
                }}>
                  {d}
                </button>
              );
            })}
          </div>

          <div style={{ marginTop: 12, paddingTop: 12, borderTop: '2px solid var(--border)', display: 'flex', gap: 10, alignItems: 'center' }}>
            <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--text2)' }}>🕐 Heure :</span>
            <input type="time" value={selTime} onChange={e => handleTimeChange(e.target.value)}
              style={{ flex: 1, height: 34, borderRadius: 10, border: '2px solid var(--border)', background: '#fff', padding: '0 10px', fontSize: 14, fontFamily: 'var(--mono)', fontWeight: 700, color: 'var(--text)', cursor: 'pointer' }} />
          </div>
        </div>
      )}
    </div>
  );
}
